/**
 * SyncService - Handles all API calls related to syncing data between backend and IndexedDB
 * This ensures IndexedDB is the single source of truth for the application
 */
import messageService from './message.service'
import { getAppConfig, updateLastSyncedAt } from '@/utils/db/appConfig'
import { setConversations } from '@/utils/db/conversations'
import { setMessages, putMessages } from '@/utils/db/messages'
import { putUsers } from '@/utils/db/users'
import userService from '@/services/api/user.service.js'
import { updateConversation } from '@/utils/db/conversations'

/**
 * Initial data sync check. Fetches initial data from backend if IndexedDB is empty.
 * @returns {Promise<void>} Resolves when sync check is complete.
 */
export const initialDataSync = async () => {
    try {
        const appConfig = await getAppConfig()
        // If we never synced, we should do a complete sync from backend to IndexedDB
        if (!appConfig.lastSyncedAt) {
            console.log(
                'Performing initial data sync from backend to IndexedDB...'
            )
            // Fetch initial conversations and messages from API
            const { conversations, messages } =
                await messageService.getInitialConversationsWithLastMessages()

            // Store data directly into IndexedDB
            if (conversations?.length > 0) {
                await setConversations(conversations)
            }
            if (messages?.length > 0) {
                await setMessages(messages)
            }

            // Sync user data after conversations are stored
            await syncUsersWithBackend()

            // Mark initial sync complete in IndexedDB
            await updateLastSyncedAt(new Date().toISOString()) // Use ISO string for consistency
            console.log('Initial data sync complete.')
            return null // For initial sync, Pinia will load fresh from DB
        }

        // If already initialized, proceed to sync recent changes from backend
        console.log('Performing background sync with backend...')
        // Ensure conversations list is up-to-date as well (handles new convos while app was closed)
        const syncedConversations = await syncConversationsWithBackend()
        const syncedMessages = await syncMessagesWithBackend()
        const syncedUsers = await syncUsersWithBackend()
        console.log('Background sync complete.')
        return { syncedConversations, syncedMessages, syncedUsers } // Return data from background sync
    } catch (error) {
        console.error('Failed during sync service operation:', error)
        // Decide if error needs propagation or specific handling
        return null
    }
}

/**
 * Perform a sync of messages with the backend (adding + updating) into IndexedDB
 * @returns {Promise<Array>} Returns the array of messages synced, or an empty array if none.
 */
const syncMessagesWithBackend = async () => {
    try {
        const config = await getAppConfig()
        const lastSyncedAt = config.lastSyncedAt

        // Only sync if lastSyncedAt exists (i.e., initial sync happened)
        if (lastSyncedAt) {
            const { messages } =
                await messageService.syncMessagesWithBackend(lastSyncedAt)
            if (messages?.length > 0) {
                console.log(
                    `Syncing ${messages.length} messages from backend to IndexedDB.`
                )
                await putMessages(messages) // Update IndexedDB
                // Update last synced time AFTER successful sync
                await updateLastSyncedAt(new Date().toISOString())
                return messages // Return the synced messages
            } else {
                console.log('No new messages from backend.')
                return [] // No messages to return
            }
        } else {
            console.warn('Skipping message sync: lastSyncedAt is not set.')
            return [] // No sync happened
        }
    } catch (error) {
        console.error('Failed to sync messages with backend:', error)
        // Decide if error needs propagation
        return [] // Error occurred
    }
}

/**
 * Perform a sync of conversations with the backend by fetching the latest
 * conversation previews and upserting them into IndexedDB. This ensures the
 * conversations list includes any newly created conversations that occurred
 * while the app was closed.
 * @returns {Promise<Array>} Returns the array of conversations synced, or an empty array if none.
 */
const syncConversationsWithBackend = async () => {
    try {
        const { conversations } =
            await messageService.getInitialConversationsWithLastMessages()
        if (conversations?.length > 0) {
            // Upsert each conversation to IndexedDB
            for (const conversation of conversations) {
                await updateConversation(conversation)
            }
            return conversations
        }
        return []
    } catch (error) {
        console.error('Failed to sync conversations with backend:', error)
        return []
    }
}

/**
 * Perform a sync of users with the backend (adding + updating) into IndexedDB
 * @returns {Promise<Array>} Returns the array of users synced, or an empty array if none.
 */
const syncUsersWithBackend = async () => {
    try {
        const usersDataResponse = await userService.getUserData()
        if (
            usersDataResponse &&
            usersDataResponse.data &&
            usersDataResponse.data.length > 0
        ) {
            await putUsers(usersDataResponse.data)
            return usersDataResponse.data
        } else {
            console.log('No new users from backend.')
            return [] // No users to return
        }
    } catch (error) {
        console.error('Failed to sync users with backend:', error)
        return [] // Error occurred
    }
}

export default {
    initialDataSync,
    // Potentially export syncMessagesWithBackend if needed to be called manually elsewhere
    // and export conversations sync for potential manual triggers
}
